import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

export default function AlertDialogTwoButton(props) {
  const { t } = useTranslation();
  const {
    open,
    onClose,
    children,
    onGoto,
    title,
    variant = 'info', // 'success', 'error', 'warning', 'info'
    showCloseIcon = true,
    primaryText,
    secondaryText,
  } = props;

  // Icon and color based on variant
  const getVariantConfig = () => {
    switch (variant) {
      case 'success':
        return {
          icon: <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#22C55E' }} />,
          bgColor: '#F0FDF4',
        };
      case 'error':
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 48, color: '#EF4444' }} />,
          bgColor: '#FEF2F2',
        };
      case 'warning':
        return {
          icon: <WarningAmberIcon sx={{ fontSize: 48, color: '#F59E0B' }} />,
          bgColor: '#FFFBEB',
        };
      default: // info
        return {
          icon: <InfoOutlinedIcon sx={{ fontSize: 48, color: '#3B82F6' }} />,
          bgColor: '#EFF6FF',
        };
    }
  };

  const config = getVariantConfig();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          maxWidth: 400,
        }
      }}
    >
      {/* Close button */}
      {showCloseIcon && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: '#9CA3AF',
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#F3F4F6',
              color: '#374151',
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      <Box sx={{ p: 4, pt: 5, textAlign: 'center' }}>
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: config.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}
        >
          {config.icon}
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: 20,
            color: '#111827',
            mb: 1.5,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {title || t('alert') || 'Alert'}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: '#6B7280',
            fontSize: 15,
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          {children}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Primary Action Button */}
          <Button
            onClick={onGoto}
            fullWidth
            variant="contained"
            sx={{
              bgcolor: '#000',
              color: '#fff',
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 15,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#1F2937',
                boxShadow: 'none',
              }
            }}
          >
            {primaryText || t('goto') || 'Go to'}
          </Button>

          {/* Secondary/Cancel Button */}
          <Button
            onClick={onClose}
            fullWidth
            sx={{
              color: '#6B7280',
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 14,
              '&:hover': {
                bgcolor: 'transparent',
                color: '#000',
              }
            }}
          >
            {secondaryText || t('cancel') || 'Cancel'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
